import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'wouter';

import { IntegrationsDesigner } from './IntegrationsPageDesigner';

const IntegrationLocationContext = createContext<{
  path: string;
  navigate: (path: string) => void;
} | null>(null);

const isUnderBase = (value: string, base: string) =>
  value === base || value.startsWith(`${base}/`);

const useIntegrationLocation = ({
  base,
}: {
  base: string;
}): [string, (path: string) => void] => {
  const location = useContext(IntegrationLocationContext);
  if (!location) throw new Error('Integration location provider is required');
  const { navigate: navigateLocation } = location;
  const navigate = useCallback(
    (path: string) => {
      if (path.startsWith('~')) {
        navigateLocation(path.slice(1));
        return;
      }
      navigateLocation(path === '/' ? base : `${base}${path}`);
    },
    [base, navigateLocation]
  );
  return [location.path.slice(base.length) || '/', navigate];
};

export const IntegrationSettingsRouter = ({ base }: { base: string }) => {
  const [browserPath, navigateBrowser] = useLocation();
  const [path, setPath] = useState(() =>
    isUnderBase(browserPath, base) ? browserPath : base
  );
  useEffect(() => {
    if (isUnderBase(browserPath, base)) {
      setPath(browserPath);
    }
  }, [browserPath, base]);
  const navigate = useCallback(
    (nextPath: string) => {
      if (isUnderBase(nextPath, base)) {
        setPath(nextPath);
        if (isUnderBase(browserPath, base)) {
          navigateBrowser(nextPath);
        }
      } else {
        navigateBrowser(`~${nextPath}`);
      }
    },
    [base, browserPath, navigateBrowser]
  );
  // Keep the router context stable when the parent rerenders without navigation.
  const location = useMemo(() => ({ path, navigate }), [path, navigate]);
  return (
    <IntegrationLocationContext.Provider value={location}>
      <IntegrationsDesigner base={base} hook={useIntegrationLocation} />
    </IntegrationLocationContext.Provider>
  );
};
